import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_BASE_URL = 'https://scagliacons.cloud/api/destinos';

function DestinosManagement() {
    const [destinos, setDestinos] = useState([]);
    const [formData, setFormData] = useState({ id: null, nombre: '', descripcion: '' });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [isError, setIsError] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        setMessage('Ingrese un nombre de destino y presione "Buscar".');
    }, []);

    const fetchDestinos = useCallback(async (term = searchTerm) => {
        const trimmedTerm = term.toLowerCase().trim();
        if (!trimmedTerm) {
            setDestinos([]);
            setMessage('Debe ingresar un término de búsqueda.');
            setIsError(true);
            return;
        }

        try {
            setLoading(true);
            setMessage('');
            setIsError(false);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}?search=${trimmedTerm}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const filteredDestinos = response.data.filter(d => 
                (d.nombre && d.nombre.toLowerCase().includes(trimmedTerm)) ||
                (d.descripcion && d.descripcion.toLowerCase().includes(trimmedTerm))
            );

            setDestinos(filteredDestinos);
            if (filteredDestinos.length === 0) {
                setMessage(`No se encontraron resultados para "${trimmedTerm}".`);
                setIsError(true);
            }
        } catch (error) {
            setMessage('Error al realizar la búsqueda.');
            setIsError(true);
        } finally {
            setLoading(false);
        }
    }, [searchTerm]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        if (isError) setIsError(false);
    };
    
    const handleSearchSubmit = (e) => {
        e.preventDefault();
        fetchDestinos();
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleEdit = (destino) => {
        setFormData(destino);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancelEdit = () => {
        setFormData({ id: null, nombre: '', descripcion: '' });
        // Si quieres que al cancelar desaparezca la búsqueda anterior:
         setDestinos([]); 
         setSearchTerm('');
    };

    const handleDelete = async (id, nombreDestino) => {
        if (!window.confirm(`¿Estás seguro de eliminar "${nombreDestino}"?`)) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_BASE_URL}/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setMessage(`Destino eliminado con éxito.`);
            setIsError(false);
            fetchDestinos(); 
        } catch (error) {
            setMessage('Error al eliminar el destino.');
            setIsError(true);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('token');
        const isUpdate = formData.id !== null;
        try {
            if (isUpdate) {
                await axios.put(`${API_BASE_URL}/${formData.id}`, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setMessage('¡Actualizado con éxito!');
            } else {
                await axios.post(API_BASE_URL, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setMessage('¡Destino creado con éxito!');
            }
            setIsError(false);
            handleCancelEdit();
            if (searchTerm.trim()) fetchDestinos();
        } catch (error) {
            setMessage('Error al procesar la solicitud.');
            setIsError(true);
        }
    };

    return (
        <div className="form-container-responsive animate-fadeIn">
            {/* Encabezado igual al anterior */}
            <div className="mb-8">
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.3em] block mb-1">Logística e Inventario</span>
                <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
                    <i className="bi bi-geo-alt-fill text-[#FFD700]"></i>
                    Gestión de Ubicaciones
                </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* COLUMNA IZQUIERDA: FORMULARIO (Se mantiene igual) */}
                <div className="lg:col-span-4">
                    <div className="bg-white p-6 rounded-[2.5rem] shadow-sm border border-gray-100 sticky top-4">
                        <div className="form-section-header !mt-0">
                            {formData.id ? 'Editar Destino' : 'Registrar Lugar'}
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                                <label className="label-modern">Nombre del Lugar</label>
                                <input type="text" className="input-modern" name="nombre" value={formData.nombre} onChange={handleChange} required placeholder="Ej: Depósito Central" />
                            </div>
                            <div>
                                <label className="label-modern">Descripción</label>
                                <textarea className="input-modern min-h-[100px] py-3" name="descripcion" value={formData.descripcion} onChange={handleChange} placeholder="Detalles..." />
                            </div>
                            <div className="flex flex-col gap-3">
                                <button type="submit" className="btn-corporate py-3 w-full">
                                    {formData.id ? 'Actualizar Registro' : 'Registrar Destino'}
                                </button>
                                {formData.id && (
                                    <button type="button" className="text-gray-500 text-xs font-bold uppercase hover:text-red-500 transition-colors py-2" onClick={handleCancelEdit}>
                                        ✕ Cancelar Edición
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>
                </div>

                {/* COLUMNA DERECHA: BÚSQUEDA Y RESULTADOS RE-DISEÑADOS */}
                <div className="lg:col-span-8">
                    <div className="bg-white p-5 rounded-[2rem] shadow-sm border border-gray-100 mb-6">
                        <form onSubmit={handleSearchSubmit} className="flex gap-3">
                            <div className="relative flex-grow">
                                <span className="absolute inset-y-0 left-0 pl-4 flex items-center text-gray-400">
                                    <i className="bi bi-search"></i>
                                </span>
                                <input type="text" className="input-modern pl-11" value={searchTerm} onChange={handleSearchChange} placeholder="Buscar destino..." />
                            </div>
                            <button type="submit" className="btn-corporate px-8 h-[48px]">Buscar</button>
                        </form>
                    </div>

                    {/* LISTA DE DESTINOS MEJORADA */}
                    <div className="space-y-4">
                        {destinos.length > 0 ? (
                            destinos.map((destino) => (
                                <div key={destino.id} className="bg-white p-4 rounded-3xl border border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:border-blue-200 transition-all shadow-sm hover:shadow-md">
                                    
                                    <div className="flex items-center gap-4 flex-grow">
                                        <div className="w-12 h-12 bg-gray-50 rounded-2xl flex items-center justify-center text-gray-400 group-hover:bg-[#FFD700]">
                                            <i className="bi bi-geo-alt-fill text-xl"></i>
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-gray-800 leading-tight">{destino.nombre}</h4>
                                            <p className="text-xs text-gray-500 mt-1">{destino.descripcion || 'Sin descripción adicional'}</p>
                                        </div>
                                    </div>

                                    {/* BOTONES DE ACCIÓN: Ahora más claros y estilizados */}
                                    <div className="flex items-center gap-2 border-t md:border-t-0 pt-3 md:pt-0">
                                        <button 
                                            onClick={() => handleEdit(destino)}
                                            className="flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-700 rounded-xl hover:bg-amber-50 hover:text-amber-600 transition-all font-bold text-xs uppercase"
                                        >
                                            <i className="bi bi-pencil-square"></i>
                                            Editar
                                        </button>
                                        <button 
                                            onClick={() => handleDelete(destino.id, destino.nombre)}
                                            className="flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-700 rounded-xl hover:bg-red-50 hover:text-red-600 transition-all font-bold text-xs uppercase"
                                        >
                                            <i className="bi bi-trash3"></i>
                                            Eliminar
                                        </button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-10 bg-gray-50 rounded-[2.5rem] border-2 border-dashed border-gray-200">
                                <p className="text-gray-400 font-medium">No hay registros para mostrar</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DestinosManagement;